// Rendered inline inside HistoryModal (not as a portal) so it sits correctly
// within Bootstrap's modal stacking context. position:fixed + high z-index
// ensures it covers the rest of the modal. stopPropagation on all click events
// prevents the overlay from inadvertently closing the parent AdminModal.

import React, { useState, useEffect } from 'react';
import { Button } from 'react-bootstrap';
import { parsePhotoUrls, formatChange } from './historyUtils';

// ─── Photo Viewer Modal ───────────────────────────────────────────────────────

export function PhotoViewerModal({ urls, onClose }) {
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  return (
    <div
      onClick={e => { e.stopPropagation(); onClose(); }}
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        background: 'rgba(0,0,0,0.82)', zIndex: 10000,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{ position: 'relative', maxWidth: '90vw', maxHeight: '90vh' }}
      >
        <img
          src={urls[current]}
          alt={`Photograph ${current + 1} of ${urls.length}`}
          style={{ maxWidth: '80vw', maxHeight: '78vh', objectFit: 'contain', borderRadius: 4, display: 'block' }}
        />
        {urls.length > 1 && (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 12, marginTop: 8 }}>
            <Button
              size="sm"
              variant="light"
              onClick={e => { e.stopPropagation(); setCurrent(i => Math.max(0, i - 1)); }}
              disabled={current === 0}
            >
              ‹
            </Button>
            <span style={{ color: '#fff' }}>{current + 1} / {urls.length}</span>
            <Button
              size="sm"
              variant="light"
              onClick={e => { e.stopPropagation(); setCurrent(i => Math.min(urls.length - 1, i + 1)); }}
              disabled={current === urls.length - 1}
            >
              ›
            </Button>
          </div>
        )}
        <button
          onClick={e => { e.stopPropagation(); onClose(); }}
          style={{
            position: 'absolute', top: -10, right: -10,
            background: '#fff', border: 'none', borderRadius: '50%',
            width: 28, height: 28, cursor: 'pointer', fontWeight: 'bold', fontSize: 16,
            lineHeight: '28px', textAlign: 'center', padding: 0,
          }}
        >
          ×
        </button>
      </div>
    </div>
  );
}

// ─── Photo eye-icon button ────────────────────────────────────────────────────

const EyeIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 16 16" fill="currentColor" style={{ verticalAlign: '-1px' }}>
    <path d="M16 8s-3-5.5-8-5.5S0 8 0 8s3 5.5 8 5.5S16 8 16 8zM1.173 8a13.133 13.133 0 0 1 1.66-2.043C4.12 4.668 5.88 3.5 8 3.5c2.12 0 3.879 1.168 5.168 2.457A13.133 13.133 0 0 1 14.828 8c-.058.087-.122.183-.195.288-.335.48-.83 1.12-1.465 1.755C11.879 11.332 10.119 12.5 8 12.5c-2.12 0-3.879-1.168-5.168-2.457A13.134 13.134 0 0 1 1.172 8z"/>
    <path d="M8 5.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5zM4.5 8a3.5 3.5 0 1 1 7 0 3.5 3.5 0 0 1-7 0z"/>
  </svg>
);

export function PhotoEyeButton({ urls, label, onViewPhotos }) {
  if (!urls || !urls.length) return <span className="text-muted">—</span>;
  const count = urls.length;
  return (
    <button
      className="btn btn-link btn-sm p-0"
      title={label || `View ${count} photo${count !== 1 ? 's' : ''}`}
      onClick={e => { e.stopPropagation(); onViewPhotos(urls); }}
      style={{ lineHeight: 1, verticalAlign: 'baseline' }}
    >
      <EyeIcon /> <small>{count} photo{count !== 1 ? 's' : ''}</small>
    </button>
  );
}

// ─── Change cell: handles plain text diffs and photo-array diffs ──────────────

export function PhotoChangeCell({ oldValue, newValue, onViewPhotos }) {
  const oldUrls = parsePhotoUrls(oldValue);
  const newUrls = parsePhotoUrls(newValue);

  if (!oldUrls && !newUrls) {
    return (
      <span style={{ fontFamily: 'monospace', fontSize: '0.85em' }}>
        {formatChange(oldValue, newValue)}
      </span>
    );
  }

  const oldPart = oldUrls
    ? <PhotoEyeButton urls={oldUrls} label="View previous photos" onViewPhotos={onViewPhotos} />
    : <span className="text-muted">—</span>;
  const newPart = newUrls
    ? <PhotoEyeButton urls={newUrls} label="View new photos" onViewPhotos={onViewPhotos} />
    : <span className="text-muted">none</span>;

  return <span>{oldPart}<span style={{ margin: '0 6px', color: '#888' }}>→</span>{newPart}</span>;
}
